import React, {useState} from 'react';
import {StyleSheet, View} from 'react-native';
import {useMutation, useQueryClient} from '@tanstack/react-query';
import {useTranslation} from 'react-i18next';

import {isoDate} from '@/entities/attendance/period';
import {invalidateSalaryDependent} from '@/entities/attendance/cache';
import {createSalary} from '@/entities/salary/api';
import {
  DEFAULT_CURRENCY,
  formatMoney,
  normalizeCurrency,
  parseMoneyToMinor,
} from '@/entities/salary/money';
import type {SalaryKind} from '@/entities/salary/types';
import {ApiError} from '@/shared/api';
import {messageOf} from '@/shared/lib/errors';
import {Banner} from '@/shared/ui/banner';
import {AppButton} from '@/shared/ui/button';
import {DateField} from '@/shared/ui/date-field';
import {AppInput} from '@/shared/ui/input';
import {Segmented} from '@/shared/ui/segmented';
import {Sheet} from '@/shared/ui/sheet';
import {AppText} from '@/shared/ui/text';
import {spacing, useTheme} from '@/shared/ui/theme';

/**
 * Объявить ставку.
 *
 * ## Почему это форма ДОБАВЛЕНИЯ и никогда не форма правки
 *
 * Правки существующей записи в API нет, и это не пробел: история отвечает на вопрос
 * «что было объявлено», а не «что мы потом переписали». Отчёт за прошлый месяц обязан
 * остаться верным после повышения — а он останется верным ровно до тех пор, пока
 * старую запись никто не отредактировал. Поэтому здесь нет «карандаша»: ошибочную
 * запись удаляют и заводят заново, повышение заводят НОВОЙ записью с датой повышения.
 *
 * ## Сумма
 *
 * Человек набирает сумы, API принимает тийины. Перевод один и живёт в
 * `entities/salary/money` — здесь только показывается, ЧТО именно уедет: строка-эхо
 * под полем печатает разобранную сумму с валютой. Деньги — единственное поле в этом
 * приложении, где «я набрал, а сохранилось другое» стоит дороже всего, и эхо дешевле
 * любого объяснения задним числом.
 *
 * Дробей больше двух знаков форма не принимает молча: округлить чужую зарплату за
 * человека — не то решение, которое стоит принимать в парсере.
 *
 * ## `409`
 *
 * Вторая запись на ту же дату — конфликт, а не тихая замена: две правды об одном дне и
 * есть то, чего история не должна допускать. Текст сервера показывается как есть — он
 * объясняет, что делать, лучше, чем это сделала бы наша формулировка.
 */
const styles = StyleSheet.create({
  form: {gap: spacing(4)},
  field: {gap: spacing(2)},
  row: {flexDirection: 'row', gap: spacing(3)},
  amount: {flex: 2},
  currency: {flex: 1, minWidth: 96},
  actions: {gap: spacing(2), marginTop: spacing(2)},
});

const KINDS: ReadonlyArray<SalaryKind> = ['monthly', 'hourly'];

export function SalaryForm({
  companyId,
  recordId,
  visible,
  onClose,
}: {
  companyId: string;
  recordId: string;
  visible: boolean;
  onClose: () => void;
}) {
  const {t} = useTranslation();
  const p = useTheme();
  const qc = useQueryClient();

  const [kind, setKind] = useState<SalaryKind>('monthly');
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState(DEFAULT_CURRENCY);
  // Сегодня, а не «день после текущей ставки»: повышение объявляют с даты повышения,
  // и угадывать её за человека здесь нечем.
  const [effectiveFrom, setEffectiveFrom] = useState(() => isoDate(new Date()));
  const [note, setNote] = useState('');

  const minor = parseMoneyToMinor(amount);
  const code = normalizeCurrency(currency);

  const save = useMutation({
    meta: {toast: 'toast.salarySaved'},
    mutationFn: () =>
      createSalary(companyId, recordId, {
        kind,
        amountMinor: minor as number,
        currency: code ?? DEFAULT_CURRENCY,
        effectiveFrom,
        note: note.trim() || undefined,
      }),
    onSuccess: async () => {
      await invalidateSalaryDependent(qc);
      onClose();
    },
  });

  const busy = save.isPending;
  const amountTouched = amount.trim().length > 0;
  const conflict = save.error instanceof ApiError && save.error.status === 409;

  return (
    <Sheet
      visible={visible}
      onClose={busy ? () => {} : onClose}
      title={t('salary.addTitle')}
      subtitle={t('salary.addSubtitle')}
      testID="salary-form">
      <View style={styles.form}>
        <View style={styles.field}>
          <AppText variant="label">{t('salary.kindLabel')}</AppText>
          <Segmented
            options={KINDS.map(value => ({value, label: t(`salary.kind.${value}`)}))}
            value={kind}
            onChange={setKind}
            disabled={busy}
            testID="salary-kind"
          />
          <AppText variant="caption">{t(`salary.kindHint.${kind}`)}</AppText>
        </View>

        <View style={styles.row}>
          <AppInput
            label={t('salary.amountLabel')}
            value={amount}
            onChangeText={setAmount}
            placeholder={t('salary.amountPlaceholder')}
            keyboardType="decimal-pad"
            autoFocus
            editable={!busy}
            error={
              amountTouched && minor == null ? t('salary.amountInvalid') : undefined
            }
            style={styles.amount}
            testID="salary-amount"
          />
          <AppInput
            label={t('salary.currencyLabel')}
            value={currency}
            onChangeText={setCurrency}
            autoCapitalize="characters"
            autoCorrect={false}
            maxLength={3}
            editable={!busy}
            error={code == null ? t('salary.currencyInvalid') : undefined}
            style={styles.currency}
            testID="salary-currency"
          />
        </View>

        {/* Эхо: ровно то число, которое уедет на сервер, в том виде, в котором оно
            вернётся в ведомость. Моноширинный — только когда есть что печатать: набранная
            им подсказка читается как данные, а не как объяснение. */}
        {minor == null ? (
          <AppText variant="caption">{t('salary.amountEchoEmpty')}</AppText>
        ) : (
          <AppText variant="mono" color={p.foreground} testID="salary-echo">
            {t('salary.amountEcho', {
              value: formatMoney(minor, code ?? DEFAULT_CURRENCY),
            })}
          </AppText>
        )}

        <View style={styles.field}>
          <DateField
            label={t('salary.effectiveFromLabel')}
            value={effectiveFrom}
            onChange={setEffectiveFrom}
            testID="salary-effective-from"
          />
          <AppText variant="caption">{t('salary.effectiveFromHint')}</AppText>
        </View>

        <AppInput
          label={t('salary.noteLabel')}
          value={note}
          onChangeText={setNote}
          placeholder={t('salary.notePlaceholder')}
          editable={!busy}
          hint={t('salary.noteHint')}
        />

        {save.isError ? (
          <Banner
            tone={conflict ? 'warning' : 'error'}
            title={messageOf(save.error, t('errors.unknown'))}
            message={conflict ? t('salary.conflictHint') : undefined}
            testID="salary-error"
          />
        ) : null}

        <View style={styles.actions}>
          <AppButton
            title={t('salary.add')}
            onPress={() => save.mutate()}
            loading={busy}
            disabled={busy || minor == null || code == null}
            testID="salary-save"
          />
          <AppButton
            title={t('actions.cancel')}
            tone="outline"
            onPress={onClose}
            disabled={busy}
          />
        </View>
      </View>
    </Sheet>
  );
}
